import React from 'react';

import * as BenchlingUtil from 'BenchlingUtil';
import {pluralize} from 'BenchlingUtil';
import {LegacyMeasurement} from 'constants/MeasurementConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {getNavigationURL} from 'nav/sidepanel/listing/SearchURLs';
import {getSelectedPageSize} from 'nav/sidepanel/PageSizeUtil';
import {getCurrentSearchId} from 'performance/getCurrentSearchInfo';
import {getStore} from 'redux-stores/Store';
import getQuantityDisplayString from 'samples/getQuantityDisplayString';
import {SearchUISelector} from 'search/reduceSearchUI';
import {FilterKey, GroupByKey, Operator, SortKey} from 'search/SearchConstants';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';

import {ExpandedItemTableQuery} from './__generated/ExpandedItemTableQuery';

type BioentityInventoryInfo = ExtractByTypename<
  ExpandedItemTableQuery['bioentities'][number],
  'BasicFolderItemNode'
>;

type ContainerOrInaccessibleNode = NonNullable<
  BioentityInventoryInfo['linkedContainerContents'][number]['container']
>;

type MixtureInventoryInfo = ExtractByTypename<ExpandedItemTableQuery['bioentities'][number], 'MixtureNode'>;

export type ContainableInventoryInfo = BioentityInventoryInfo | MixtureInventoryInfo;

interface Props {
  containableInventoryInfo?: ContainableInventoryInfo;
  sampleSearchLink?: string | null;
}

interface ContainerStats {
  countBySchema: {[schemaName: string]: number};
  countBySchemaAndQuantity: {[schemaName: string]: {[quantity: string]: number}};
  inaccessibleCount: number;
}

function gatherStats(aggregatedContainers: Array<ContainerOrInaccessibleNode>): ContainerStats {
  const countBySchema: {[schemaName: string]: number} = {};
  const countBySchemaAndQuantity: {[schemaName: string]: {[quantity: string]: number}} = {};
  let inaccessibleCount = 0;

  for (const container of aggregatedContainers) {
    if (isInaccessible(container)) {
      inaccessibleCount++;
    } else {
      const quantity: LegacyMeasurement | null = container.quantityJson;
      const quantityString =
        quantity != null && quantity.value != null && quantity.units != null
          ? `${quantity.value}${quantity.units}`
          : `unspecified ${getQuantityDisplayString(false, false)}`;
      const schemaName = container.storableSchema.name;

      countBySchema[schemaName] = (countBySchema[schemaName] ?? 0) + 1;

      countBySchemaAndQuantity[schemaName] = countBySchemaAndQuantity[schemaName] ?? {};
      countBySchemaAndQuantity[schemaName][quantityString] =
        (countBySchemaAndQuantity[schemaName][quantityString] ?? 0) + 1;
    }
  }

  return {countBySchema, countBySchemaAndQuantity, inaccessibleCount};
}

const PLURALIZABLES_REGEX = /(^|\s)(tube|well|vial|flask|bottle|container)$/i;

function buildSummarySubstring(schemaName: string, quantity: string, count: number): string {
  if (schemaName.match(PLURALIZABLES_REGEX)) {
    return `${count} ${pluralize(count, schemaName)} @ ${quantity}`;
  } else {
    return `${schemaName}: ${count} @ ${quantity}`;
  }
}

/**
 * Returns keys in descending order by numeric value. In case of ties, orders by key (ascending).
 *
 * Example:
 *   Input: {bottle: 1, vial: 2, flask: 2, tube: 3}
 *   Output: ['tube', 'flask', 'vial', 'bottle']
 */
function dictionaryKeysOrderedByValueDesc(countBySchema: {[schemaName: string]: number}): Array<string> {
  return Object.entries(countBySchema)
    .sort(([schemaNameA, countA], [schemaNameB, countB]) =>
      countA < countB ? 1 : countA > countB ? -1 : schemaNameA.localeCompare(schemaNameB)
    )
    .map(([schemaName, count]) => schemaName);
}

function computeSummaries({countBySchema, countBySchemaAndQuantity, inaccessibleCount}: ContainerStats): {
  oneLineSummary: string;
  tooltipSummary: string;
} {
  const perSchemaSummaries: Array<string> = [];
  const perSchemaAndQuantitySummaries: Array<string> = [];

  for (const schemaName of dictionaryKeysOrderedByValueDesc(countBySchema)) {
    const quantities = dictionaryKeysOrderedByValueDesc(countBySchemaAndQuantity[schemaName]);
    for (const quantity of quantities) {
      perSchemaAndQuantitySummaries.push(
        buildSummarySubstring(schemaName, quantity, countBySchemaAndQuantity[schemaName][quantity])
      );
    }
    if (quantities.length === 1) {
      perSchemaSummaries.push(
        buildSummarySubstring(schemaName, quantities[0], countBySchemaAndQuantity[schemaName][quantities[0]])
      );
    } else {
      perSchemaSummaries.push(
        buildSummarySubstring(
          schemaName,
          `varied ${getQuantityDisplayString(false, true)}`,
          countBySchema[schemaName]
        )
      );
    }
  }
  if (inaccessibleCount > 0) {
    const substring = `${inaccessibleCount} ${pluralize(inaccessibleCount, 'container')} (no access)`;
    perSchemaSummaries.push(substring);
    perSchemaAndQuantitySummaries.push(substring);
  }

  return {
    oneLineSummary: perSchemaSummaries.join(', '),
    tooltipSummary: perSchemaAndQuantitySummaries.join('\n'),
  };
}

function isContainable(containableInventoryInfo: ContainableInventoryInfo | undefined): boolean {
  return (
    containableInventoryInfo?.tagSchema?.isContainable ||
    containableInventoryInfo?.tagSchema?.childEntitySchemas.some((tagSchema) => tagSchema.isContainable) ||
    false
  );
}

function getContainedItemIds(containableInventoryInfo: ContainableInventoryInfo): Array<string> {
  return containableInventoryInfo.tagSchema?.isContainable
    ? [containableInventoryInfo.id]
    : containableInventoryInfo.allChildEntities.map((childEntity) => childEntity.id);
}

function buildLink(containableInventoryInfo: ContainableInventoryInfo): string {
  const containedItemIds: Array<string> = getContainedItemIds(containableInventoryInfo);

  return getNavigationURL(
    '/search',
    {
      limit: getSelectedPageSize(),
      offset: 0,
      query: '',
      sorts: [{key: SortKey.SORT_MODIFIED_AT_KEY, reverse: false}],
      filters: [
        {
          key: FilterKey.TYPES,
          operator: Operator.IS_ONE_OF,
          value: [ModelTypeConstants.CONTAINER, ModelTypeConstants.PLATE],
        },
        {
          key: FilterKey.CONTAINER_CONTENTS,
          operator: Operator.HAS_ONE_OF,
          value: {
            contentIds: containedItemIds,
          },
        },
      ],
    },
    [null, null]
  );
}

function isSupportedInSampleSearch(containableInventoryInfo?: ContainableInventoryInfo): boolean {
  return isContainable(containableInventoryInfo);
}

export function buildSampleSearchLink(
  itemId: string,
  containableInventoryInfoById?: {
    [bioentityId: string]: ContainableInventoryInfo;
  }
): string | null {
  // This is wasteful in that we refetch the selected items/build a link for every selected containable, when
  // the selection and the link won't change. However, the ListingRenderer interface is such that it calls
  // a render method for each row, with no natural place to share the computed info without making the data
  // flow undesirably circuitous. Until we have time to refactor, we are accepting this repeated work to keep
  // cohesion higher.

  if (!containableInventoryInfoById || !isSupportedInSampleSearch(containableInventoryInfoById[itemId])) {
    return null;
  }

  const searchId = getCurrentSearchId()!;
  const selector = new SearchUISelector(getStore().getState());
  const selectedIds = selector.getSelectedIdsForSearch(searchId);

  // If the item is not a selected item, we don't want to override with a sample search link, instead
  // leaving the old behavior of drilling through to just its inventory.
  if (!selectedIds.find((id) => id === itemId)) {
    return null;
  }

  const selectedContainableIds = selectedIds.filter((id) =>
    isSupportedInSampleSearch(containableInventoryInfoById[id])
  );

  const containedItemIds: Array<string> = selectedContainableIds
    .map((id) => getContainedItemIds(containableInventoryInfoById[id]))
    .flat();

  return getNavigationURL(
    '/search',
    {
      limit: getSelectedPageSize(),
      offset: 0,
      query: '',
      sorts: [{key: SortKey.SORT_MODIFIED_AT_KEY, reverse: false}],
      filters: [
        {
          key: FilterKey.TYPES,
          operator: Operator.IS_ONE_OF,
          value: [ModelTypeConstants.CONTAINER, ModelTypeConstants.PLATE],
        },
        {
          key: FilterKey.CONTAINER_CONTENTS,
          operator: Operator.HAS_ONE_OF,
          value: {
            contentIds: containedItemIds,
          },
        },
      ],
      groupBy: {
        groupField: GroupByKey.CONTAINER_CONTENTS,
        childOffset: 0,
        childLimit: 0,
      },
    },
    [null, null]
  );
}

export default function ContainableInventorySummary({
  containableInventoryInfo,
  sampleSearchLink,
}: Props): JSX.Element | null {
  if (containableInventoryInfo == null || !isContainable(containableInventoryInfo)) {
    return null;
  }

  const aggregatedContainers: Array<ContainerOrInaccessibleNode> = [
    ...(containableInventoryInfo?.linkedContainerContents ?? []),
    ...(containableInventoryInfo?.tagSchema?.isContainable
      ? [] // only include child entities if parent entity is not containable
      : (containableInventoryInfo?.allChildEntities ?? [])
    ).flatMap((childEntityContainerInfo) => childEntityContainerInfo.linkedContainerContents ?? []),
  ].flatMap((content) => (content.container != null ? [content.container] : []));

  if (aggregatedContainers.length === 0) {
    return (
      <Tooltip title="No inventory available" delay={300} placement={TooltipPlacement.LEFT}>
        <span>No inventory available</span>
      </Tooltip>
    );
  }

  const stats = gatherStats(aggregatedContainers);
  const {oneLineSummary, tooltipSummary} = computeSummaries(stats);
  // If we are using sample search, the link will be overridden with a search link that goes to the
  // group by view for all selected items.
  const inventorySearchLink = sampleSearchLink || buildLink(containableInventoryInfo);

  return (
    <Tooltip title={tooltipSummary} delay={300} placement={TooltipPlacement.LEFT}>
      <a
        href={inventorySearchLink}
        onClick={(e) => {
          BenchlingUtil.navigate(e, inventorySearchLink);
          e.stopPropagation();
        }}
      >
        {oneLineSummary}
      </a>
    </Tooltip>
  );
}
